import React, { useEffect, useState } from 'react';
import { TransformWrapper, TransformComponent } from 'react-zoom-pan-pinch';
import { RedditPost, RedditPostProps } from './RedditPost';

interface CanvasViewProps {
  posts: RedditPostProps[];
  onClose: () => void;
  activeTags: string[];
  commentedPosts: Set<string>;
  onCommentPosted: (postId: string, commentId: string, subreddit: string, commentText: string) => Promise<boolean> | boolean;
  isAuthenticated: boolean;
}

interface GroupedPosts {
  [keyword: string]: RedditPostProps[];
}

export const CanvasView: React.FC<CanvasViewProps> = ({ posts, onClose, activeTags, commentedPosts, onCommentPosted, isAuthenticated }) => {
  const [groupedPosts, setGroupedPosts] = useState<GroupedPosts>({});

  useEffect(() => {
    console.log('Canvas View - Received posts:', posts.length);
    console.log('Canvas View - Active tags:', activeTags);
    
    // Initialize groups for all active tags
    const grouped: GroupedPosts = {};
    activeTags.forEach(tag => {
      grouped[tag] = [];
    });

    // Assign posts to each tag group if they match the tag
    posts.forEach(post => {
      const postText = `${post.title} ${post.selftext}`.toLowerCase();
      activeTags.forEach(tag => {
        if (postText.includes(tag.toLowerCase())) {
          grouped[tag].push({
            ...post,
            keywords: activeTags
          });
        }
      });
    });

    console.log('Grouped posts:', Object.keys(grouped).map(k => `${k}: ${grouped[k].length} posts`));
    setGroupedPosts(grouped);
  }, [posts, activeTags]);

  // Only show the empty state if there are no posts at all
  if (posts.length === 0) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
        <div className="bg-white p-8 rounded-lg shadow-lg max-w-md w-full">
          <h2 className="text-xl font-bold mb-4">No Posts to Display</h2>
          <p className="text-gray-600 mb-4">There are no posts to display in the canvas view. Try selecting some keywords first.</p>
          <button
            onClick={onClose}
            className="w-full bg-sky-500 text-white px-4 py-2 rounded-lg hover:bg-sky-600"
          >
            Close Canvas
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50">
      {/* Static Background Pattern */}
      <div 
        className="absolute inset-0 pointer-events-none"
        style={{
          backgroundColor: '#f3f4f6',
          backgroundImage: `
            radial-gradient(circle at 1px 1px, #9ca3af 1px, transparent 0)
          `,
          backgroundSize: '24px 24px',
          backgroundPosition: '-1px -1px'
        }}
      />

      {/* Controls Overlay */}
      <div className="absolute top-4 right-4 z-[60] flex items-center gap-4">
        <button
          onClick={onClose}
          className="bg-white text-black px-4 py-2 rounded-lg shadow-lg hover:bg-gray-100"
        >
          Close Canvas
        </button>
      </div>
      
      <div className="absolute inset-0">
        <TransformWrapper
          initialScale={1}
          initialPositionX={0}
          initialPositionY={0}
          minScale={0.1}
          maxScale={2}
          wheel={{ 
            disabled: false,
            step: 0.1,
            wheelDisabled: false,
            touchPadDisabled: false,
            activationKeys: ['Control', 'Meta']
          }}
          limitToBounds={false}
          centerZoomedOut={true}
          centerOnInit={true}
          smooth={true}
          alignmentAnimation={{ sizeX: 0, sizeY: 0 }}
          velocityAnimation={{ sensitivity: 0 }}
          doubleClick={{ disabled: true }}
        >
          {({ zoomIn, zoomOut, resetTransform }) => (
            <>
              {/* Zoom Controls */}
              <div className="absolute bottom-4 right-4 z-[60] flex items-center gap-2">
                <button
                  onClick={() => zoomIn()}
                  className="bg-white text-black w-10 h-10 rounded-lg shadow-lg hover:bg-gray-100 flex items-center justify-center"
                >
                  +
                </button>
                <button
                  onClick={() => zoomOut()}
                  className="bg-white text-black w-10 h-10 rounded-lg shadow-lg hover:bg-gray-100 flex items-center justify-center"
                >
                  -
                </button>
                <button
                  onClick={() => resetTransform()}
                  className="bg-white text-black px-4 h-10 rounded-lg shadow-lg hover:bg-gray-100"
                >
                  Reset
                </button>
              </div>

              <TransformComponent
                wrapperStyle={{
                  width: '100%',
                  height: '100%',
                  overflow: 'hidden'
                }}
                contentStyle={{
                  width: 'max-content',
                  height: 'max-content',
                  padding: '4rem'
                }}
              >
                <div 
                  className="canvas-content" 
                  style={{
                    display: 'grid',
                    gridTemplateColumns: `repeat(${Object.keys(groupedPosts).length}, 384px)`,
                    gap: '2rem',
                    minWidth: '100vw',
                    minHeight: '100vh',
                    background: 'transparent',
                    position: 'relative',
                    zIndex: 1
                  }}
                >
                  {Object.entries(groupedPosts).map(([keyword, posts]) => (
                    <div
                      key={keyword}
                      className="bg-white rounded-lg shadow-lg overflow-hidden"
                      style={{ 
                        isolation: 'isolate',
                        height: 'fit-content',
                        position: 'relative',
                        zIndex: 2
                      }}
                    >
                      <div className="sticky top-0 bg-sky-500 text-white p-4 shadow-md z-10">
                        <h2 className="text-xl font-bold">{keyword}</h2>
                        <p className="text-sm opacity-75">{posts.length} posts</p>
                      </div>
                      <div className="p-4 space-y-4 bg-white relative">
                        {posts.map((post) => (
                          <div 
                            key={post.id} 
                            className="transform transition-transform hover:scale-102"
                            style={{ 
                              isolation: 'isolate',
                              position: 'relative',
                              zIndex: 3
                            }}
                          >
                            <div className="pointer-events-auto relative z-10 bg-white">
                              <RedditPost
                                key={post.id}
                                {...post}
                                hasCommented={commentedPosts.has(post.id)}
                                onCommentPosted={onCommentPosted}
                                position={post.position}
                                zIndex={post.zIndex}
                                isCanvasView={true}
                                isAuthenticated={isAuthenticated}
                              />
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </TransformComponent>
            </>
          )}
        </TransformWrapper>
      </div>
    </div>
  );
}; 